/**
 * leaderboard.js — Super Effective
 * Multi-select characters, PSA grades, column sort, character search
 */
(function () {
  'use strict';

  const app = document.getElementById('lb-app');
  if (!app) return;

  const API_URL = app.dataset.apiUrl || 'https://superleaderboard.replit.app/api/leaderboard';
  const RETRY_DELAY_MS = 4000;

  const CHAR_EMOJI = {
    'Charizard': '🔥',
    'Pikachu': '⚡',
    'Blastoise': '💧',
    'Venusaur': '🌿',
    'Mewtwo': '👁️',
    'Mew': '✨',
    'Lugia': '🌪️',
    'Ho-Oh': '🌈',
    'Umbreon': '🌙',
    'Espeon': '☀️',
    'Gengar': '👻',
    'Gyarados': '🐉',
    'Snorlax': '💤',
    'Alakazam': '🥄',
    'Eevee': '🦊'
  };

  /* ── STATE ── */
  let serverData = { cards: [], total: 0, page: 1, limit: 10, pages: 1 };
  let allFetchedData = null;
  let leaderboardSetMeta = [];
  const setFilterMatchers = new Map();
  const EEVEE_FAMILY = new Set([
    'Eevee', 'Vaporeon', 'Jolteon', 'Flareon', 'Espeon',
    'Umbreon', 'Leafeon', 'Glaceon', 'Sylveon'
  ]);

  const state = {
    characters: ['all'],   // array — multi-select
    grades: ['all'],       // array — multi-select
    set: 'all',
    priceMin: 0,
    priceMax: Infinity,
    sort: 'marketCost',
    sortDir: 'desc',
    timeRange: 'week',
    searchQuery: '',
    page: 1,
    limit: 10,
    visibleCount: 10
  };

  /* ── DOM REFS ── */
  const elLoading   = document.getElementById('lb-loading');
  const elError     = document.getElementById('lb-error');
  const elEmpty     = document.getElementById('lb-empty');
  const elTable     = document.getElementById('lb-table');
  const elRows      = document.getElementById('lb-rows');
  const elCount     = document.getElementById('lb-count');
  const elUpdated   = document.getElementById('lb-updated');
  const elTrendHead = document.getElementById('trend-header');
  const elSortColTrend = document.getElementById('sort-col-trend');

  // Global image error fallback
  document.addEventListener('error', function(e) {
    if (e.target && e.target.tagName === 'IMG' && e.target.classList.contains('lb-card-img')) {
      const char = e.target.dataset.character;
      const emoji = CHAR_EMOJI[char] || '🃏';
      e.target.outerHTML = `<div class="lb-card-img-placeholder">${emoji}</div>`;
    }
  }, true);

  /* ── UTILS ── */
  function hide(el) { if (el) el.style.display = 'none'; }
  function show(el, t) { if (el) el.style.display = t || 'block'; }

  function showLoading() { show(elLoading, 'flex'); hide(elError); hide(elEmpty); hide(elTable); }
  function showError()   { hide(elLoading); show(elError); hide(elEmpty); hide(elTable); }
  function showEmpty()   { hide(elLoading); hide(elError); show(elEmpty); hide(elTable); }
  function showTable()   { hide(elLoading); hide(elError); hide(elEmpty); show(elTable); }

  function formatPrice(val) {
    if (!Number.isFinite(Number(val)) || Number(val) <= 0) return '—';
    val = Number(val);
    if (val >= 1000000) return '$' + Number((val / 1000000).toFixed(2)) + 'M';
    if (val >= 1000)    return '$' + Number((val / 1000).toFixed(1)) + 'K';
    return '$' + val.toLocaleString();
  }

  function marketValue(card) {
    const value = Number(card?.lastSoldPrice ?? card?.marketCost ?? 0);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  function formatMarketValue(card) {
    const value = marketValue(card);
    return value > 0 ? formatPrice(value) : 'No recent sales';
  }

  function baseCardId(cardOrId) {
    const id = typeof cardOrId === 'string' ? cardOrId : cardOrId?.id;
    return String(id || '').replace(/-psa\d+$/i, '');
  }

  function gradeNumber(grade) {
    const match = String(grade || '').match(/PSA\s*(\d+)/i);
    return match ? Number(match[1]) : 0;
  }

  function resetVisibleCards() {
    state.page = 1;
    state.visibleCount = state.limit;
  }

  function normaliseSetName(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/^ex\s+/, '')
      .replace(/&/g, ' and ')
      .replace(/\band\b/g, '')
      .replace(/[^a-z0-9]+/g, '');
  }

  function isBaseSetCard(card) {
    return card.set === 'Base Set'
      || card.set === 'Base'
      || card.set === '1st Edition Base Set'
      || card.set === 'Shadowless Base Set'
      || card.set === 'Base Set Unlimited';
  }

  // Build the sidebar from actual leaderboard cards, retaining the friendly
  // catalog label while mapping PriceCharting/PPT naming differences.
  function buildSetFilterMatchers(metaSets, cards) {
    setFilterMatchers.clear();
    const availableSetNames = new Set();

    metaSets.forEach(metaSet => {
      let matches;
      if (metaSet.name === '1st Edition Base Set') {
        matches = card => card.set === '1st Edition Base Set'
          || (isBaseSetCard(card) && /\b1st\s*edition\b/i.test(String(card.variant || '')));
      } else if (metaSet.name === 'Shadowless Base Set') {
        matches = card => card.set === 'Shadowless Base Set'
          || (isBaseSetCard(card) && /\bshadowless\b/i.test(String(card.variant || '')));
      } else if (metaSet.name === 'Base Set Unlimited') {
        matches = card => card.set === 'Base Set Unlimited'
          || (isBaseSetCard(card) && !/\b(?:1st\s*edition|shadowless)\b/i.test(String(card.variant || '')));
      } else {
        const catalogNames = [metaSet.name, ...(metaSet.aliases || [])]
          .map(normaliseSetName)
          .filter(Boolean);
        matches = card => {
          const normCardSet = normaliseSetName(card.set);
          return catalogNames.some(cn => normCardSet === cn || normCardSet.includes(cn) || cn.includes(normCardSet));
        };
      }

      setFilterMatchers.set(metaSet.name, matches);
      if (cards.some(matches)) {
        availableSetNames.add(metaSet.name);
      }
    });

    return availableSetNames;
  }

  // The Base Set choices in the sidebar are print variants, while card.set
  // remains the canonical set name and card.variant carries the printing.
  function matchesSetFilter(card, selectedSet) {
    const matcher = setFilterMatchers.get(selectedSet);
    if (matcher) return matcher(card);
    return card.set === selectedSet || normaliseSetName(card.set) === normaliseSetName(selectedSet);
  }

  function matchesCharacterFilter(card, selectedCharacter) {
    if (selectedCharacter === 'Eevee') return EEVEE_FAMILY.has(card.character);
    return card.character === selectedCharacter;
  }

  function formatTrend(val, grade) {
    if (grade && grade !== 'PSA 10') return { text: '—', cls: 'lb-trend--neutral' };
    if (val === null || val === undefined) return { text: '—', cls: 'lb-trend--neutral' };
    if (Math.abs(val) < 0.05) val = 0;
    const sign  = val > 0 ? '+' : '';
    const cls   = val > 0 ? 'lb-trend--up' : val < 0 ? 'lb-trend--down' : 'lb-trend--neutral';
    const arrow = val > 0 ? '▲' : val < 0 ? '▼' : '–';
    return { text: `${arrow} ${sign}${Math.abs(val).toFixed(1)}%`, cls };
  }

  function gradeClass(grade) {
    if (!grade) return '';
    const g = grade.toLowerCase().replace(/\s/g, '');
    if (g === 'psa10') return 'lb-grade-badge--psa10';
    if (g === 'psa9')  return 'lb-grade-badge--psa9';
    if (g === 'psa8')  return 'lb-grade-badge--psa8';
    if (g === 'psa7')  return 'lb-grade-badge--psa7';
    if (/^psa[1-6]$/.test(g)) return 'lb-grade-badge--lower';
    return '';
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return 'Updated ' + new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  }

  function esc(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  function escAttr(str) {
    return String(str).replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }

  /* ── MEDAL HTML ── */
  function rankHTML(rank) {
    if (rank === 1) return `<div class="lb-medal lb-medal--gold" aria-label="1st place">1</div>`;
    if (rank === 2) return `<div class="lb-medal lb-medal--silver" aria-label="2nd place">2</div>`;
    if (rank === 3) return `<div class="lb-medal lb-medal--bronze" aria-label="3rd place">3</div>`;
    return `<div class="lb-rank-badge">${rank}</div>`;
  }

  const API_BASE = new URL(API_URL, window.location.origin).origin;
  const CURATED_LEADERBOARD_URL = API_URL;
  const DYNAMIC_SEARCH_URL = `${API_BASE}/api/search`;

  // The verified leaderboard catalog is the source of truth for every view.
  // Filters narrow this same ranked universe; they must never switch to an
  // active-listing search that can hide a real card or replace its value.
  function shouldUseCuratedLeaderboard() {
    return true;
  }

  function dynamicSortValue() {
    if (state.sort === 'marketCost') return state.sortDir === 'asc' ? 'price_asc' : 'price_desc';
    if (state.sort === 'popularityScore') return 'listings';
    if (state.sort === 'trendWeek') return state.sortDir === 'asc' ? 'trendWeek_asc' : 'trendWeek_desc';
    if (state.sort === 'trendMonth') return state.sortDir === 'asc' ? 'trendMonth_asc' : 'trendMonth_desc';
    return 'price_desc';
  }

  function dynamicCharacters() {
    const selected = state.characters.filter(character => character !== 'all');
    const expanded = selected.flatMap(character => character === 'Eevee' ? [...EEVEE_FAMILY] : [character]);
    return [...new Set(expanded)];
  }

  function dynamicSearchUrl(isRefresh = false) {
    const params = new URLSearchParams({
      grade: state.grades.includes('all') ? 'all' : state.grades.join(','),
      sort: dynamicSortValue(),
      language: 'english',
      page: '1',
      limit: '100'
    });
    const characters = dynamicCharacters();
    if (characters.length) params.set('character', characters.join(','));
    if (state.set && state.set !== 'all') params.set('set', state.set);
    if (state.searchQuery.trim()) params.set('q', state.searchQuery.trim());
    if (state.priceMin > 0) params.set('minPrice', String(state.priceMin));
    if (Number.isFinite(state.priceMax)) params.set('maxPrice', String(state.priceMax));
    if (isRefresh) params.set('refresh', '1');
    return `${DYNAMIC_SEARCH_URL}?${params.toString()}`;
  }

  async function fetchCards(isRefresh = false) {
    let url = shouldUseCuratedLeaderboard()
      ? (isRefresh ? `${API_BASE}/api/leaderboard/refresh` : CURATED_LEADERBOARD_URL)
      : dynamicSearchUrl(isRefresh);
    // The API owns exact catalog expansion. Passing the selected set lets it
    // retrieve the provider's full, verified set catalog—not a marketplace
    // name search—and the browser still filters that stable result locally.
    if (shouldUseCuratedLeaderboard() && state.set && state.set !== 'all') {
      const params = new URLSearchParams({ set: state.set });
      url += `${url.includes('?') ? '&' : '?'}${params.toString()}`;
    }
    const res = await fetch(url, { mode: 'cors', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`API error ${res.status}`);
    allFetchedData = await res.json();
    allFetchedData.source = shouldUseCuratedLeaderboard() ? 'curated' : 'dynamic';
    return allFetchedData;
  }

  async function fetchSets() {
    try {
      const res = await fetch(`${API_BASE}/api/meta/sets`, { mode: 'cors' });
      return await res.json();
    } catch { return { sets: [] }; }
  }

  async function fetchGrades() {
    try {
      const res = await fetch(`${API_BASE}/api/meta/grades`, { mode: 'cors' });
      return await res.json();
    } catch { return { grades: [] }; }
  }

  /* ── FETCH & FILTER ── */
  async function applyFilters(isRefresh = false) {
    const btn = document.getElementById('lb-refresh-btn');
    if (isRefresh && btn) btn.classList.add('spinning');
    else if (!isRefresh) showLoading();

    try {
      const data = await fetchCards(isRefresh);
      // Every state change narrows the same verified catalog. The browser only
      // handles filtering, presentation sorting, and one-card-per-rank
      // deduplication on that stable result universe.
      let filtered = data.cards || [];

      if (leaderboardSetMeta.length) buildSetFilterMatchers(leaderboardSetMeta, data.cards || []);

      if (!state.characters.includes('all')) {
        filtered = filtered.filter(card =>
          state.characters.some(character => matchesCharacterFilter(card, character)),
        );
      }

      if (state.set && state.set !== 'all') {
        filtered = filtered.filter(card => matchesSetFilter(card, state.set));
      }

      const searchQuery = state.searchQuery.trim().toLowerCase();
      if (searchQuery) {
        filtered = filtered.filter(card => [
          card.character,
          card.name,
          card.set,
          card.variant,
          card.cardNumber,
        ].some(value => String(value || '').toLowerCase().includes(searchQuery)));
      }

      // The curated API intentionally returns every PSA grade together so a
      // grade-only filter still ranks against the verified global universe.
      if (!state.grades.includes('all')) {
        filtered = filtered.filter(card => state.grades.includes(card.grade));
      }
      filtered = filtered.filter(card => {
        const value = marketValue(card);
        return value >= state.priceMin && value <= state.priceMax;
      });

      // 2. SORT
      filtered.sort((a, b) => {
        let valA, valB;
        if (state.sort === 'marketCost') {
          valA = marketValue(a);
          valB = marketValue(b);
          // Unknown values are not $0 sales; keep them below priced cards in
          // either direction so the ranking never promotes missing data.
          if (!valA && valB) return 1;
          if (valA && !valB) return -1;
        }
        else if (state.sort === 'popularityScore') { valA = a.popularityScore || 0; valB = b.popularityScore || 0; }
        else if (state.sort === 'psaPopulation') { valA = a.psaPopulation || 0; valB = b.psaPopulation || 0; }
        else if (state.sort === 'trendWeek') { valA = a.trendWeek || 0; valB = b.trendWeek || 0; }
        else if (state.sort === 'trendMonth') { valA = a.trendMonth || 0; valB = b.trendMonth || 0; }
        else if (state.sort === 'name') { valA = a.name || ''; valB = b.name || ''; }
        
        if (valA < valB) return state.sortDir === 'asc' ? -1 : 1;
        if (valA > valB) return state.sortDir === 'asc' ? 1 : -1;
        // Stable card-level tie breakers keep the best grade representative
        // deterministic when All Grades or multiple grades are selected.
        const priceDiff = marketValue(b) - marketValue(a);
        if (priceDiff !== 0) return priceDiff;
        return gradeNumber(b.grade) - gradeNumber(a.grade);
      });

      // A leaderboard rank belongs to an exact card, not to every grade copy
      // of that card. Because the list is already sorted, keeping the first
      // record chooses that card's strongest representative for the active
      // ranking/filter while preserving one unique card per rank.
      const seenCards = new Set();
      filtered = filtered.filter(card => {
        const id = baseCardId(card);
        if (!id || seenCards.has(id)) return false;
        seenCards.add(id);
        return true;
      });

      // 3. PAGINATE METADATA
      const total = filtered.length;
      const pages = Math.ceil(total / state.limit) || 1;
      state.visibleCount = Math.min(Math.max(state.visibleCount, state.limit), Math.max(total, state.limit));

      serverData = {
        cards: filtered,
        total: total,
        page: state.page,
        limit: state.limit,
        pages: pages,
        updatedAt: data.updatedAt
      };

      if (elUpdated) elUpdated.textContent = formatDate(serverData.updatedAt);
      render();
    } catch (err) {
      console.warn('[Leaderboard] API error:', err.message);
      showError();
    } finally {
      if (btn) btn.classList.remove('spinning');
    }
  }

  window.fetchEbayPrice = async function(btnEl, cardId) {
    try {
      btnEl.innerHTML = '<span class="lb-spinner"></span>';
      btnEl.disabled = true;
      
      // Keep the PSA suffix so the live eBay lookup uses the exact grade.
      const res = await fetch(`${API_BASE}/api/ebay-price/${encodeURIComponent(cardId)}`, { mode: 'cors' });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      
      const priceHtml = data.lowestPrice ? formatPrice(data.lowestPrice) : 'N/A';
      const container = btnEl.closest('.lb-col--lowest');
      if (data.ebayListingUrl) {
        container.innerHTML = `<a href="${data.ebayListingUrl}" target="_blank" class="lb-price lb-price--ebay" title="View on eBay">${priceHtml}</a>`;
      } else {
        container.innerHTML = `<span class="lb-price">${priceHtml}</span>`;
      }
    } catch (e) {
      console.error(e);
      btnEl.innerHTML = 'Error';
      btnEl.disabled = false;
    }
  };

  function gradeLadderHTML(card) {
    // Live marketplace searches return one exact active listing at a time.
    // A grade ladder only makes sense for the precomputed card records that
    // contain a stable card ID spanning PSA 1–10.
    if (String(card?.id || '').startsWith('search-')) return '';
    const id = baseCardId(card);
    const gradeCards = (allFetchedData?.cards || [])
      .filter(item => baseCardId(item) === id)
      .sort((a, b) => gradeNumber(b.grade) - gradeNumber(a.grade));

    if (!gradeCards.length) return '';

    return `
      <div class="lb-grade-ladder" hidden>
        <div class="lb-grade-ladder__header">
          <span>PSA grade market ladder</span>
          <span>Real grade-level data · unavailable prices are not estimated</span>
        </div>
        <div class="lb-grade-ladder__grid">
          ${gradeCards.map(item => {
            const value = marketValue(item);
            const source = item.priceSource === 'Unavailable' ? 'No recent sales data' : item.priceSource || 'Market data';
            const confidence = item.priceConfidence ? ` · ${item.priceConfidence} confidence` : '';
            return `
              <div class="lb-grade-ladder__item" title="${escAttr(source + confidence)}">
                <span class="lb-grade-badge ${gradeClass(item.grade)}">${esc(item.grade)}</span>
                <strong class="${value ? '' : 'lb-price--unavailable'}">${value ? formatPrice(value) : 'No sales'}</strong>
                <small>Pop. ${item.psaPopulation != null ? item.psaPopulation.toLocaleString() : '—'}</small>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  /* ── RENDER ── */
  function render() {
    if (!serverData.cards || serverData.cards.length === 0) {
      showEmpty();
      if (elCount) elCount.textContent = '0';
      const elPagination = document.getElementById('lb-pagination');
      if (elPagination) elPagination.innerHTML = '';
      return;
    }
    showTable();
    if (elCount) elCount.textContent = serverData.total;

    // Update trend column label
    const emoji = '📈';
    const trendKey = 'Trend %';
    if (elTrendHead) elTrendHead.innerHTML = `<span class="lb-emoji">${emoji}</span>${trendKey}`;
    if (elSortColTrend) elSortColTrend.dataset.colSort = 'trendWeek';

    const frag = document.createDocumentFragment();

    const startIndex = 0;
    const cardsToRender = serverData.cards.slice(0, state.visibleCount);

    cardsToRender.forEach((card, i) => {
      const rank     = startIndex + i + 1;
      const trendVal = state.timeRange === 'week' ? card.trendWeek : card.trendMonth;
      const trend    = formatTrend(trendVal, card.grade);
      const emoji    = CHAR_EMOJI[card.character] || '🃏';
      const value    = marketValue(card);
      const isLarge  = value >= 100000;
      const identityLabel = [card.set, card.variant, card.cardNumber].filter(Boolean).join(' · ');
      const ladderHtml = gradeLadderHTML(card);

      const lowestVal = card.lowestPrice ?? value;
      const isLowestLarge = lowestVal >= 100000;
      
      let lowestHtml = '';
      if (card.lowestPrice !== null && card.lowestPrice !== undefined) {
        lowestHtml = `<span class="lb-price ${isLowestLarge ? 'lb-price--large' : ''}">${formatPrice(lowestVal)}</span>`;
      } else {
        lowestHtml = `
          <div class="lb-lowest-group">
            <span class="lb-price lb-price--ppt" title="PPT Market Cost">${formatPrice(lowestVal)}</span>
            <button class="lb-ebay-btn" style="opacity: 0.6; color: #fff; display: inline-flex; align-items: center; justify-content: center; background: none; border: none; padding: 2px; cursor: pointer; margin-left: 4px;" title="Check Live eBay" onclick="fetchEbayPrice(this, '${escAttr(card.id)}')">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                <path d="M3 3v5h5"></path>
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                <path d="M16 21v-5h5"></path>
              </svg>
            </button>
          </div>
        `;
      }

      let imageUrl = card.image;
      if (!imageUrl && card.id) {
        // Fallback: Construct pokemontcg.io CDN URL from ID (e.g. "ex15-100-psa10")
        const parts = card.id.split('-');
        if (parts.length >= 2) {
           imageUrl = `https://images.pokemontcg.io/${parts[0]}/${parts[1]}.png`;
        }
      }

      const row = document.createElement('div');
      row.className = 'lb-card-row';
      row.dataset.rank = rank;

      row.innerHTML = `
        <div class="lb-col lb-col--rank">${rankHTML(rank)}</div>

        <div class="lb-col lb-col--img">
          ${imageUrl
            ? `<span class="lb-card-img-frame"><img class="lb-card-img" src="${escAttr(imageUrl)}" data-character="${escAttr(card.character)}" alt="${escAttr(card.name)}" loading="lazy"></span>`
            : `<div class="lb-card-img-placeholder">${emoji}</div>`
          }
        </div>

        <div class="lb-col lb-col--info">
          <div class="lb-card-info">
            <div class="lb-card-name"><span class="lb-rank-mobile">${rankHTML(rank)}</span>${esc(card.name)}</div>
            <div class="lb-card-set">${esc(identityLabel)}</div>
          </div>
        </div>

        <div class="lb-col lb-col--grade">
          <span class="lb-price-mobile ${value ? '' : 'lb-price--unavailable'}">${formatMarketValue(card)}</span>
          ${ladderHtml
            ? `<button type="button" class="lb-grade-badge lb-grade-toggle ${gradeClass(card.grade)}" data-grade-toggle aria-expanded="false" title="View all PSA grades for this card">
                ${esc(card.grade || '—')}<span aria-hidden="true">⌄</span>
              </button>`
            : `<span class="lb-grade-badge ${gradeClass(card.grade)}">${esc(card.grade || '—')}</span>`
          }
        </div>

        <div class="lb-col lb-col--price">
          <span class="lb-price ${isLarge ? 'lb-price--large' : ''} ${value ? '' : 'lb-price--unavailable'}">${formatMarketValue(card)}</span>
        </div>

        <div class="lb-col lb-col--lowest">
          ${lowestHtml}
        </div>

        <div class="lb-col lb-col--trend">
          <span class="lb-trend ${trend.cls}">${trend.text}</span>
        </div>

        <div class="lb-col lb-col--pop">
          <span class="lb-psa-pop">${card.psaPopulation != null ? card.psaPopulation.toLocaleString() : 'Unknown'}</span>
        </div>

        ${ladderHtml}
      `;

      row.querySelector('[data-grade-toggle]')?.addEventListener('click', (event) => {
        event.stopPropagation();
        const button = event.currentTarget;
        const ladder = row.querySelector('.lb-grade-ladder');
        if (!ladder) return;
        const isOpen = ladder.hasAttribute('hidden');
        ladder.toggleAttribute('hidden', !isOpen);
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        row.classList.toggle('lb-card-row--expanded', isOpen);
      });

      frag.appendChild(row);
    });

    elRows.innerHTML = '';
    elRows.appendChild(frag);

    renderPagination();
  }

  /* ── UPDATE SORT ARROW VISUALS ── */
  function updateSortArrows() {
    const appContainer = document.getElementById('lb-app');
    if (appContainer) appContainer.setAttribute('data-active-sort', state.sort);

    document.querySelectorAll('.lb-col--sortable').forEach(col => {
      const isActive = col.dataset.colSort === state.sort
        || (state.sort === 'trendMonth' && col.dataset.colSort === 'trendWeek')
        || (state.sort === 'trendWeek'  && col.id === 'sort-col-trend');

      col.classList.toggle('lb-col--sort-active', isActive);
      col.classList.toggle('lb-col--sort-asc',    isActive && state.sortDir === 'asc');
      col.classList.toggle('lb-col--sort-desc',   isActive && state.sortDir === 'desc');
    });
  }

  /* ── FILTER PILLS (Event Delegation) ── */
  function bindPillEvents(containerId, stateKey, isMulti) {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.addEventListener('click', (e) => {
      const pill = e.target.closest('.lb-pill');
      if (!pill) return;
      
      const value = pill.dataset.value;
      if (!value) return;

      if (!isMulti) {
        container.querySelectorAll('.lb-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        state[stateKey] = value;
      } else {
        if (value === 'all') {
          state[stateKey] = ['all'];
          container.querySelectorAll('.lb-pill').forEach(p => {
            p.classList.toggle('active', p.dataset.value === 'all');
          });
        } else {
          state[stateKey] = state[stateKey].filter(x => x !== 'all');
          container.querySelector('.lb-pill[data-value="all"]')?.classList.remove('active');
          
          if (state[stateKey].includes(value)) {
            state[stateKey] = state[stateKey].filter(x => x !== value);
            pill.classList.remove('active');
          } else {
            state[stateKey].push(value);
            pill.classList.add('active');
          }
          
          if (state[stateKey].length === 0) {
            state[stateKey] = ['all'];
            container.querySelector('.lb-pill[data-value="all"]')?.classList.add('active');
          }
        }
      }
      
      resetVisibleCards();
      applyFilters();
    });
  }

  bindPillEvents('filter-character', 'characters', true);
  bindPillEvents('filter-set', 'set', false);
  bindPillEvents('filter-grade', 'grades', true);

  /* ── CHARACTER SEARCH ── */
  const charSearchEl = document.getElementById('lb-char-search');
  if (charSearchEl) {
    charSearchEl.addEventListener('input', () => {
      state.searchQuery = charSearchEl.value;
      resetVisibleCards();
      applyFilters();
    });
  }

  /* ── SORT TABS ── */
  document.querySelectorAll('.lb-sort-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const s = tab.dataset.sort;
      if (state.sort === s) {
        state.sortDir = state.sortDir === 'desc' ? 'asc' : 'desc';
      } else {
        state.sort = s;
        state.sortDir = 'desc';
      }
      document.querySelectorAll('.lb-sort-tab').forEach(t => t.classList.toggle('active', t === tab));
      resetVisibleCards();
      updateSortArrows();
      applyFilters();
    });
  });

  /* ── COLUMN HEADER SORT ── */
  document.querySelectorAll('.lb-col--sortable').forEach(col => {
    col.addEventListener('click', () => {
      const colSort = col.dataset.colSort;
      if (state.sort === colSort) {
        state.sortDir = state.sortDir === 'desc' ? 'asc' : 'desc';
      } else {
        state.sort = colSort;
        state.sortDir = colSort === 'name' ? 'asc' : 'desc';
      }
      // Sync sort tab highlight
      document.querySelectorAll('.lb-sort-tab').forEach(t => {
        const isMatch = t === col || t.dataset.sort === state.sort || (t.dataset.sort === 'trendWeek' && state.sort === 'trendMonth');
        t.classList.toggle('active', isMatch);
      });
      resetVisibleCards();
      updateSortArrows();
      applyFilters();
    });
  });

  /* ── TIME RANGE (custom dropdown) ── */
  const timeRangeEl     = document.getElementById('lb-timerange');
  const timeRangeBtn    = document.getElementById('lb-timerange-btn');
  const timeRangeDD     = document.getElementById('lb-timerange-dropdown');
  const timeRangeDisplay = document.getElementById('lb-timerange-display');

  function applyTimeRange(value) {
    state.timeRange = value;
    if (timeRangeEl) timeRangeEl.value = value;
    if (timeRangeDisplay) timeRangeDisplay.textContent = value === 'week' ? 'This Week' : 'This Month';
    // update option active states
    if (timeRangeDD) {
      timeRangeDD.querySelectorAll('.lb-timerange-option').forEach(opt => {
        const isActive = opt.dataset.value === value;
        opt.classList.toggle('active', isActive);
        opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
    if (state.sort === 'trendWeek' || state.sort === 'trendMonth') {
      state.sort = state.timeRange === 'week' ? 'trendWeek' : 'trendMonth';
      document.querySelectorAll('.lb-sort-tab').forEach(t => {
        const isMatch = t.dataset.sort === state.sort || (t.dataset.sort === 'trendWeek' && state.sort === 'trendMonth');
        t.classList.toggle('active', isMatch);
      });
    }
    resetVisibleCards();
    updateSortArrows();
    applyFilters();
  }

  if (timeRangeBtn && timeRangeDD) {
    timeRangeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const open = timeRangeDD.classList.toggle('open');
      timeRangeBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    timeRangeDD.querySelectorAll('.lb-timerange-option').forEach(opt => {
      opt.addEventListener('click', () => {
        timeRangeDD.classList.remove('open');
        timeRangeBtn.setAttribute('aria-expanded', 'false');
        applyTimeRange(opt.dataset.value);
      });
    });

    document.addEventListener('click', () => {
      timeRangeDD.classList.remove('open');
      timeRangeBtn.setAttribute('aria-expanded', 'false');
    });
  } else if (timeRangeEl) {
    // Fallback: original select
    timeRangeEl.addEventListener('change', () => applyTimeRange(timeRangeEl.value));
  }


  /* ── PRICE RANGE FILTER ── */
  const elPriceMin = document.getElementById('lb-price-min');
  const elPriceMax = document.getElementById('lb-price-max');
  const elPresets = document.querySelectorAll('.lb-preset-btn');

  function clearPresetsActive() {
    elPresets.forEach(btn => btn.classList.remove('active'));
  }

  function handlePriceInputChange() {
    clearPresetsActive();
    const minVal = parseFloat(elPriceMin.value);
    const maxVal = parseFloat(elPriceMax.value);
    
    state.priceMin = !isNaN(minVal) ? minVal : 0;
    state.priceMax = !isNaN(maxVal) ? maxVal : Infinity;
    resetVisibleCards();
    applyFilters();
  }

  if (elPriceMin) {
    elPriceMin.addEventListener('input', handlePriceInputChange);
  }
  if (elPriceMax) {
    elPriceMax.addEventListener('input', handlePriceInputChange);
  }

  elPresets.forEach(btn => {
    btn.addEventListener('click', () => {
      const isActive = btn.classList.contains('active');
      
      if (elPriceMin) elPriceMin.value = '';
      if (elPriceMax) elPriceMax.value = '';
      
      if (isActive) {
        btn.classList.remove('active');
        state.priceMin = 0;
        state.priceMax = Infinity;
      } else {
        clearPresetsActive();
        btn.classList.add('active');
        state.priceMin = parseFloat(btn.dataset.min) || 0;
        
        const maxVal = btn.dataset.max;
        state.priceMax = (maxVal === 'Infinity' || !maxVal) ? Infinity : parseFloat(maxVal);
      }
      resetVisibleCards();
      applyFilters();
    });
  });

  /* ── CLEAR ALL FILTERS ── */
  document.getElementById('lb-clear-filters')?.addEventListener('click', () => {
    state.characters = ['all'];
    state.grades      = ['all'];
    state.set         = 'all';
    state.grade       = 'all';
    state.priceMin    = 0;
    state.priceMax    = Infinity;
    state.searchQuery = '';
    
    if (elPriceMin) elPriceMin.value = '';
    if (elPriceMax) elPriceMax.value = '';
    if (charSearchEl) charSearchEl.value = '';
    clearPresetsActive();
    
    document.querySelectorAll('.lb-pill').forEach(p => p.classList.toggle('active', p.dataset.value === 'all'));
    resetVisibleCards();
    applyFilters();
  });

  /* ── REFRESH + RETRY ── */
  document.getElementById('lb-refresh-btn')?.addEventListener('click', () => applyFilters(true));
  document.getElementById('lb-retry-btn')?.addEventListener('click', () => applyFilters(false));

  /* ── SEE MORE / SEE LESS TOGGLES ── */
  document.querySelectorAll('.lb-see-more-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const targetId = btn.dataset.target;
      const targetEl = document.getElementById(targetId);
      if (!targetEl) return;

      const isCollapsed = targetEl.classList.toggle('collapsed');
      btn.textContent = isCollapsed ? '+ See more' : '- See less';

      if (targetId === 'filter-set') {
        const wrappers = targetEl.querySelectorAll('.lb-filter-era-sets');
        wrappers.forEach(w => w.classList.toggle('collapsed', isCollapsed));
        const headers = targetEl.querySelectorAll('.lb-filter-era-header');
        headers.forEach(h => h.classList.toggle('expanded', !isCollapsed));
      }
    });
  });

  /* ── RENDER PAGINATION ── */
  function renderPagination() {
    const elPagination = document.getElementById('lb-pagination');
    if (!elPagination) return;

    const shown = Math.min(state.visibleCount, serverData.total);
    if (shown >= serverData.total) {
      elPagination.innerHTML = '';
      return;
    }

    elPagination.innerHTML = `
      <div class="lb-pag-status">Showing ${shown} of ${serverData.total}</div>
      <button class="lb-show-more-btn" type="button">
        Show 10 more
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
    `;

    elPagination.querySelector('.lb-show-more-btn')?.addEventListener('click', () => {
      state.visibleCount = Math.min(state.visibleCount + state.limit, serverData.total);
      render();
    });
  }

  function initBeamsAnimation() {
    const canvas = document.getElementById('lb-beams-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w, h;
    function resize() {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    }
    window.addEventListener('resize', resize);
    resize();

    const beams = [];
    const beamCount = 35;
    for (let i = 0; i < beamCount; i++) {
      beams.push({
        side: Math.random() < 0.5 ? 'left' : 'right',
        xRatio: Math.random(),
        y: Math.random() * 110 - 5,
        speed: Math.random() * 0.15 + 0.05,
        length: Math.random() * 50 + 40,
        width: Math.random() * 1.5 + 1.0,
        type: Math.random() < 0.25 ? 'secondary' : 'primary'
      });
    }

    const elSortBar = document.querySelector('.lb-sort-bar');

    function animate() {
      ctx.clearRect(0, 0, w, h);

      let limitY = 280; // default fallback
      if (elSortBar) {
        limitY = elSortBar.getBoundingClientRect().bottom;
      }

      const contentWidth = 1420;
      const margin = (w - contentWidth) / 2;

      beams.forEach(b => {
        b.y -= b.speed;
        if (b.y < -15) {
          b.y = 105;
          b.xRatio = Math.random();
        }

        let sx;
        if (margin > 80) {
          // Large screen: place beams in margins (left margin - 20px, right margin - 20px)
          if (b.side === 'left') {
            sx = b.xRatio * (margin - 20);
          } else {
            sx = margin + contentWidth + 20 + b.xRatio * (margin - 20);
          }
        } else {
          // Small screen: place beams on the outer 10% of screen edges
          if (b.side === 'left') {
            sx = (b.xRatio * 0.10) * w;
          } else {
            sx = (0.90 + b.xRatio * 0.10) * w;
          }
        }

        const sy = (b.y / 100) * h;

        let edgeAlpha = 1;
        // Fade out above the sort bar bottom limit
        if (sy < limitY) {
          edgeAlpha = 0;
        } else if (sy < limitY + 120) {
          edgeAlpha = Math.max(0, (sy - limitY) / 120);
        }

        // Faint fade at bottom edge too
        if (sy > h - 100) {
          edgeAlpha = Math.min(edgeAlpha, Math.max(0, (h - sy) / 100));
        }

        if (edgeAlpha > 0) {
          const color = b.type === 'secondary' ? '#ec5b13' : '#00e5ff';
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.6 * edgeAlpha;
          ctx.shadowBlur = 10 * edgeAlpha;
          ctx.shadowColor = color;
          ctx.fillRect(sx, sy, b.width, b.length);
        }
      });

      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1.0;
      requestAnimationFrame(animate);
    }
    animate();
  }

  /* ── INIT ── */
  async function init() {
    updateSortArrows();
    initBeamsAnimation();
    
    // Fetch filter meta
    try {
      const [setsMeta, gradesMeta] = await Promise.all([
        fetchSets(),
        fetchGrades(),
      ]);
      leaderboardSetMeta = setsMeta.sets || [];
      
      const setContainer = document.getElementById('filter-set');
      if (setContainer && setsMeta.sets) {
        const byEra = setsMeta.sets.reduce((acc, s) => {
          (acc[s.era] = acc[s.era] || []).push(s);
          return acc;
        }, {});
        
        Object.entries(byEra).forEach(([era, eraSets]) => {
          if (era === 'Japanese') return;

          let targetContainer = setContainer;
          
          if (era && era !== 'undefined') {
            const eraWrapper = document.createElement('div');
            eraWrapper.className = 'lb-filter-era-wrapper';
            
            const header = document.createElement('div');
            header.className = 'lb-filter-era-header';
            
            const eraIcon = eraSets[0]?.eraIcon;
            if (eraIcon) {
              header.innerHTML = `<img src="${eraIcon}?v=2" class="lb-filter-era-icon" alt="${era}" />`;
            } else {
              header.textContent = era;
            }
            
            const setsWrapper = document.createElement('div');
            setsWrapper.className = 'lb-filter-era-sets collapsed';

            header.addEventListener('click', () => {
              header.classList.toggle('expanded');
              setsWrapper.classList.toggle('collapsed');
            });
            
            eraWrapper.appendChild(header);
            eraWrapper.appendChild(setsWrapper);
            setContainer.appendChild(eraWrapper);
            
            targetContainer = setsWrapper;
          }
          
          eraSets.forEach(set => {
            const btn = document.createElement('button');
            btn.className = 'lb-pill';
            btn.dataset.filter = 'set';
            btn.dataset.value = set.name;
            btn.dataset.era = era;
            if (set.symbol) {
              btn.innerHTML = `<img src="${set.symbol}" class="lb-filter-set-icon" alt="" /><span>${set.name}</span>`;
            } else {
              btn.innerHTML = `<span>${set.name}</span>`;
            }
            targetContainer.appendChild(btn);
          });
        });
      }

      const gradeContainer = document.getElementById('filter-grade');
      if (gradeContainer && gradesMeta.grades) {
        gradesMeta.grades.forEach(grade => {
          const btn = document.createElement('button');
          btn.className = 'lb-pill lb-pill--grade';
          btn.dataset.filter = 'grade';
          btn.dataset.value = grade.value;
          btn.innerHTML = `<span class="lb-grade-num">${esc(grade.value)}</span><span class="lb-grade-sub">${esc(grade.label)}</span>`;
          gradeContainer.appendChild(btn);
        });
      }
    } catch (e) {
      console.warn('[Leaderboard] Failed to load meta for filters', e);
    }

    applyFilters(false);

    /* ── MOBILE FILTER TOGGLE ── */
    const filterToggle = document.getElementById('lb-filter-toggle');
    const sidebarBody  = document.getElementById('lb-sidebar-body');
    const sidebar      = document.getElementById('lb-sidebar');
    if (filterToggle && sidebarBody) {
      filterToggle.addEventListener('click', () => {
        const isOpen = sidebarBody.classList.toggle('open');
        filterToggle.setAttribute('aria-expanded', isOpen);
        sidebar.classList.toggle('filters-open', isOpen);
      });
    }
  }

  init();

})();
